import { Button, Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { X } from 'lucide-react';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import styles from '@/components/InformationModal/InformationModal.module.css';
import { useHostContext } from '@/context/HostContext.tsx';
import { cn } from '@/utils';

type TInformationModalProps = {
	closeModal: () => void;
	isOpenedModal: boolean;
	title: ReactNode;
};

const InformationModal: FC<PropsWithChildren<TInformationModalProps>> = ({
	closeModal,
	isOpenedModal,
	title,
	children
}) => {
	const isMobile = useMediaQuery('(max-width: 768px)');
	const { isMiniCreatorMode } = useHostContext();

	return (
		<Modal
			centered={!isMobile && !isMiniCreatorMode}
			classNames={{
				header: '!py-0',
				body: cn('!px-[32px] !pb-[32px]', {
					'!px-4 flex-1 overflow-auto': isMiniCreatorMode
				}),
				title: 'w-full',
				overlay: cn({ 'absolute inset-0 bg-black/35': isMiniCreatorMode }),
				inner: cn({ 'absolute inset-0 p-0 h-full': isMiniCreatorMode }),
				content: cn({
					'h-full max-h-full w-full overflow-hidden flex flex-col':
						isMiniCreatorMode
				})
			}}
			fullScreen={isMobile}
			lockScroll={!isMiniCreatorMode}
			onClose={closeModal}
			opened={isOpenedModal}
			radius={isMobile || isMiniCreatorMode ? 0 : '24'}
			size={isMobile || isMiniCreatorMode ? '100%' : 'auto'}
			styles={{
				root: { '--mb-z-index': 1005 },
				inner: { '--mb-z-index': 1005 },
				overlay: { '--mb-z-index': 1005 }
			}}
			title={
				<div className="flex items-center gap-3 w-full justify-between">
					{title}

					<Button
						onClick={closeModal}
						p={0}
						variant="transparent"
					>
						<X className="!w-[20px] !h-[20px]" />
					</Button>
				</div>
			}
			withCloseButton={false}
			withinPortal={!isMiniCreatorMode}
		>
			<article className={cn(styles.article, '[font-family:var(--font-body)]')}>
				{children}
			</article>
		</Modal>
	);
};

export default InformationModal;
